import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import { stringify } from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import useDebounce from '@dashboard/hooks/useDebounce';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import {
  useAdminTemplates,
  useAdminTemplateCategoriesAll,
} from '@dashboard/hooks/queries/useAdminQueries';
import { useUpdateTemplate, useDeleteTemplate } from '@dashboard/hooks/queries/useAdminMutations';

import { confirm } from '@dashboard/lib/confirm';

import Button from '@dashboard/components/Button';
import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { FormElement, Label, TextInput } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Link from '@dashboard/components/Link';

import Circle from '../components/Circle';

const Templates = () => {
  const history = useHistory();

  const query = useUrlQuery();

  const { searchInput, setSearchInput, search } = useSearchInput(query.search);
  const [sortBy, setSortBy] = useState(query.sortBy || 'order');
  const [sortDirection, setSortDirection] = useState(
    query.sortDirection ? Number(query.sortDirection) : 1,
  );
  const [category, setCategory] = useState(query.category || '');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        ...(category && { category }),
        ...(search && { search }),
      }),
    });
  }, [sortBy, sortDirection, category, search]);

  // Use centralized hook for fetching categories (for dropdown)
  const { data: categories } = useAdminTemplateCategoriesAll();

  // Build filters object for templates query
  const filters = {
    sortBy,
    sortDirection,
    ...(category && { category }),
    ...(search && { search }),
    limit: 20,
  };

  // Use centralized hook for fetching templates
  const { data, fetchNextPage, isLoading } = useAdminTemplates(filters, {
    refetchOnMount: true,
  });

  // Use centralized mutation hooks
  const updateTemplateMutation = useUpdateTemplate({
    onError: error => {
      window.alert(error.message);
    },
  });

  const deleteTemplateMutation = useDeleteTemplate({
    onError: error => {
      window.alert(error.message);
    },
  });

  // Convert sortBy/sortDirection to TanStack SortingState
  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  // Handle sorting changes from DataGrid
  const handleSortingChange = updater => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  // Flatten pages data for DataGrid
  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // Define columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Title" />,
        cell: ({ row }) => (
          <Link href={`/admin/templates/${row.original._id}`}>{row.getValue('title')}</Link>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'enabled',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Enabled" />,
        cell: ({ row }) => <Circle color={row.getValue('enabled') ? 'green' : 'red'} />,
        enableSorting: true,
      },
      {
        accessorKey: 'category.slug',
        id: 'category.slug',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Category" />,
        cell: ({ row }) => row.original.category?.title,
        enableSorting: true,
      },
      {
        accessorKey: 'usageCount',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Usage count" />,
        cell: ({ row }) => row.getValue('usageCount') || 0,
        enableSorting: true,
      },
      {
        accessorKey: 'order',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Order" />,
        cell: ({ row }) => (
          <OrderCell
            template={row.original}
            onOrderChange={values => {
              updateTemplateMutation.mutate({
                _id: row.original._id,
                values,
              });
            }}
          />
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Date created" />,
        cell: ({ row }) => (
          <div className="flex items-center">
            <div className="flex-1">
              {format(new Date(row.getValue('createdAt')), 'd MMM, yyyy')}
            </div>
            <Button
              onClick={async () => {
                const proceed = await confirm('Delete this template?');

                if (!proceed) return;

                deleteTemplateMutation.mutate(row.original._id);
              }}
              appearance="bare"
              className="ml-2"
              size="small"
            >
              <TrashIcon className="w-4 h-4" />
            </Button>
          </div>
        ),
        enableSorting: true,
      },
    ],
    [updateTemplateMutation, deleteTemplateMutation],
  );

  // Filter controls as custom actions
  const filterControls = (
    <div className="flex items-end gap-x-2">
      <FormElement className="!mb-0">
        <Label htmlFor="search">Search</Label>
        <div className="w-64">
          <div className="relative flex items-center">
            <TextInput
              border
              name="search"
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by name, email, ID"
              value={searchInput}
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="flex items-center absolute right-0 top-0 bottom-0 p-1.5 m-px bg-white dark:bg-background text-gray-300 dark:text-muted-foreground hover:text-gray-400 dark:hover:text-muted-foreground transition-colors rounded"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </FormElement>

      <FormElement className="!mb-0">
        <Select
          name="category"
          value={category || 'all'}
          onValueChange={value => setCategory(value === 'all' ? '' : value)}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories?.map(currentCategory => (
              <SelectItem key={currentCategory._id} value={currentCategory._id}>
                {upperFirst(currentCategory.title)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormElement>

      <Button href={`/admin/templates/new`} appearance="primary">
        New template
      </Button>
    </div>
  );

  return (
    <div className="pr-4">
      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        toolbar={{
          customActions: filterControls,
        }}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No templates found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

function OrderCell({ template, onOrderChange }) {
  const [order, setOrder] = useState(template.order);
  const debouncedOrder = useDebounce(order, 300);

  useEffect(() => {
    if (debouncedOrder === template.order) return;

    onOrderChange({
      order: debouncedOrder,
    });
  }, [debouncedOrder]);

  return (
    <TextInput
      type="number"
      value={order}
      onChange={e => {
        setOrder(e.target.value);
      }}
    />
  );
}

export default Templates;
